import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, take } from 'rxjs';
import {
  ReportingApiService,
  ReportStatus,
  ReportStatusResponseDto,
  SendReportResponseDto,
} from 'src/app/shared/services/reporting-api.service';
import { AppState } from '../store/state';
import { isPresent } from '../utils/isPresent';
import { selectAskingReportStatus } from 'src/app/modules/documents/store/selectors';
import { DocumentsActions } from 'src/app/modules/documents/store/actions';
import { GetDocumentDto } from 'src/app/modules/documents/models/documents.model';
import { sendReportFailedNotification, sendReportSuccessNotification } from '../utils/report.utils';
import { TranslocoService } from '@ngneat/transloco';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ReportWorker {
  private interval: any;
  private reportTrackingId: string = '';
  private reportPollingFrequencySeconds: number = 10;
  private reportTimeoutSeconds: number = 600;
  private document!: GetDocumentDto;

  constructor(
    private store$: Store<AppState>,
    private reportingApiService: ReportingApiService,
    private translocoService: TranslocoService,
  ) {
    this.store$
      .select(selectAskingReportStatus)
      .pipe(untilDestroyed(this), filter(isPresent), distinctUntilChanged())
      .subscribe((askingReportStatus) => {
        if (askingReportStatus === true) {
          this.startAskingReportUpdates();
        }
      });
  }

  init(sendReportResponse: SendReportResponseDto, doc: GetDocumentDto): void {
    this.reportTrackingId = sendReportResponse.reportTrackingId;
    this.reportPollingFrequencySeconds = sendReportResponse.reportPollingFrequencySeconds;
    this.reportTimeoutSeconds = sendReportResponse.reportTimeoutSeconds;
    this.document = doc;
  }

  public startAskingReportUpdates(): void {
    this.addLogInfo();

    let start = new Date().getTime();

    this.interval = setInterval(() => {
      const now = new Date().getTime();
      let timeSpent = (now - start) / 1000;

      if (timeSpent > this.reportTimeoutSeconds) {
        console.log(
          `askReportsUpdates stopped by timeout: ${this.reportTrackingId} timeSpent: ${timeSpent}s / ${this.reportTimeoutSeconds}s`,
        );
        sendReportFailedNotification(this.store$, this.translocoService, this.document);
        this.stopWorker(ReportStatus.Error); // |Timeout
        return;
      }

      this.reportingApiService
        .getReportStatus$(this.reportTrackingId)
        .pipe(take(1))
        .subscribe((response) => {
          this.processReportStatusResponse(timeSpent, response);
        });
    }, this.reportPollingFrequencySeconds * 1000);
  }

  addLogInfo(): void {
    console.log(`Background worker is running`);
    console.log(`reportTrackingId: ${this.reportTrackingId}`);
    console.log(`reportPollingFrequencySeconds: ${this.reportPollingFrequencySeconds}`);
    console.log(`reportTimeoutSeconds: ${this.reportTimeoutSeconds}`);
  }

  processReportStatusResponse(timeSpent: number, response: ReportStatusResponseDto): void {
    const reportStatus = response.status;

    switch (reportStatus) {
      case ReportStatus.InProgress: {
        console.log(
          `askReportsUpdates InProgress: ${this.reportTrackingId} timeSpent: ${timeSpent}s / ${this.reportTimeoutSeconds}s`,
        );
        break;
      }
      case ReportStatus.Success: {
        console.log(
          `askReportsUpdates Success: ${this.reportTrackingId} timeSpent: ${timeSpent}s / ${this.reportTimeoutSeconds}s`,
        );
        sendReportSuccessNotification(this.store$, this.translocoService, this.document);
        this.stopWorker(reportStatus);
        break;
      }
      case ReportStatus.Error: {
        console.log(
          `askReportsUpdates Error: ${this.reportTrackingId} timeSpent: ${timeSpent}s / ${this.reportTimeoutSeconds}s`,
        );
        sendReportFailedNotification(this.store$, this.translocoService, this.document);
        this.stopWorker(reportStatus);
        break;
      }
    }
  }

  stopWorker(reportStatus: ReportStatus) {
    clearInterval(this.interval);

    switch (reportStatus) {
      case ReportStatus.Success: {
        this.store$.dispatch(DocumentsActions.sendReportCompleteSuccess());
        break;
      }
      case ReportStatus.Error: {
        this.store$.dispatch(DocumentsActions.sendReportCompleteFailed());
        break;
      }
    }
  }
}
